/** Rasterize a rectangular REGION of the scene — the "circle & ask AI" lasso's
 * eye. Excalidraw's exporters fit to whole elements, so a region crop is
 * composed by hand: the view background, the slice of any locked page-backdrop
 * image under the region (the PDF content being circled), then the drawn
 * elements intersecting it (exported transparent and positioned by their
 * common bounds). Returns a PNG dataURL, or null for a degenerate region. */

import type { BinaryFileData, BinaryFiles, ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { NonDeletedExcalidrawElement } from "@excalidraw/excalidraw/element/types";

type ExcalidrawModule = typeof import("@excalidraw/excalidraw");

export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MAX_DIM_PX = 1600;
const MIN_REGION = 8; // scene units — anything smaller is a stray tap

function intersect(a: Region, e: { x: number; y: number; width: number; height: number }): Region | null {
  const x0 = Math.max(a.x, e.x);
  const y0 = Math.max(a.y, e.y);
  const x1 = Math.min(a.x + a.w, e.x + e.width);
  const y1 = Math.min(a.y + a.h, e.y + e.height);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("could not decode a region image"));
    img.src = src;
  });
}

export async function captureRegion(
  api: ExcalidrawImperativeAPI,
  module: ExcalidrawModule,
  region: Region,
): Promise<string | null> {
  if (region.w < MIN_REGION || region.h < MIN_REGION) return null;
  const scale = Math.min(2, MAX_DIM_PX / Math.max(region.w, region.h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(region.w * scale));
  canvas.height = Math.max(1, Math.round(region.h * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = api.getAppState().viewBackgroundColor || "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const files: BinaryFiles = api.getFiles();
  const live = api.getSceneElements() as readonly NonDeletedExcalidrawElement[];

  // 1. The page backdrop slices under the region (PDF pages are locked images).
  for (const el of live) {
    if (el.type !== "image" || !el.locked) continue;
    const inter = intersect(region, el);
    if (!inter) continue;
    const fileId = (el as { fileId?: string | null }).fileId;
    const file = fileId ? files[fileId as BinaryFileData["id"]] : undefined;
    if (!file) continue;
    try {
      const img = await loadImage(file.dataURL);
      const sx = ((inter.x - el.x) / el.width) * img.naturalWidth;
      const sy = ((inter.y - el.y) / el.height) * img.naturalHeight;
      const sw = (inter.w / el.width) * img.naturalWidth;
      const sh = (inter.h / el.height) * img.naturalHeight;
      ctx.drawImage(
        img, sx, sy, sw, sh,
        (inter.x - region.x) * scale, (inter.y - region.y) * scale,
        inter.w * scale, inter.h * scale,
      );
    } catch {
      /* an undecodable backdrop slice — keep composing the rest */
    }
  }

  // 2. The drawn elements intersecting the region, exported transparent and
  // placed by their common bounds (the region canvas clips the excess).
  const drawn = live.filter(
    (el) => !(el.type === "image" && el.locked) && intersect(region, el),
  );
  if (drawn.length) {
    try {
      const blob = await module.exportToBlob({
        elements: drawn,
        files,
        mimeType: "image/png",
        appState: { exportBackground: false },
        exportPadding: 0,
      });
      const url = URL.createObjectURL(blob);
      try {
        const overlay = await loadImage(url);
        const [x1, y1, x2, y2] = module.getCommonBounds(drawn);
        ctx.drawImage(
          overlay,
          (x1 - region.x) * scale, (y1 - region.y) * scale,
          Math.max(1, x2 - x1) * scale, Math.max(1, y2 - y1) * scale,
        );
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      /* stroke overlay is best-effort — the backdrop alone is still useful */
    }
  }

  return canvas.toDataURL("image/png");
}
